import { useRef } from "react";
import {
  motion,
  useMotionValue,
  useSpring,
  useTransform,
  useScroll,
} from "framer-motion";
import { ArrowUpRight, Calculator } from "lucide-react";
import KineticHeadline from "@/components/KineticHeadline";
import MarqueeStrip from "@/components/MarqueeStrip";
import activationAreaImg from "@/assets/dholera-activation-area.png";
import { Button } from "@/components/ui/button";

const STATS = [
  { value: "920", label: "SQ KM NOTIFIED SIR" },
  { value: "06", label: "TOWN PLANNING SCHEMES" },
  { value: "₹3,900", label: "STARTING ₹ / SQ YD" },
  { value: "2026", label: "AIRPORT OPERATIONS" },
];

const fadeUp = (delay) => ({
  initial: { opacity: 0, y: 24 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.8, delay, ease: [0.16, 1, 0.3, 1] },
});

export default function Hero({ onNavigate, onOpenCalc }) {
  const ref = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const sx = useSpring(mx, { stiffness: 60, damping: 20 });
  const sy = useSpring(my, { stiffness: 60, damping: 20 });
  const frameX = useTransform(sx, [-0.5, 0.5], [14, -14]);
  const frameY = useTransform(sy, [-0.5, 0.5], [10, -10]);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 90]);

  const handleMouse = (e) => {
    const r = ref.current.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  const lines = [
    <>The future</>,
    <span className="text-outline">isn&apos;t coming.</span>,
    <>
      It&apos;s being <span className="text-accent">plotted.</span>
    </>,
  ];

  return (
    <section
      ref={ref}
      onMouseMove={handleMouse}
      className="relative overflow-hidden pt-28 md:pt-36"
      data-testid="hero-section"
    >
      <motion.div
        aria-hidden
        className="pointer-events-none absolute -right-48 -top-48 hidden h-[520px] w-[520px] rounded-full border border-border lg:block"
        animate={{ rotate: 360 }}
        transition={{ duration: 160, repeat: Infinity, ease: "linear" }}
      >
        <span className="absolute left-1/2 top-0 h-4 w-px -translate-x-1/2 bg-accent" />
        <span className="absolute left-1/2 top-1/2 h-px w-1/2 bg-border" />
      </motion.div>

      <div className="mx-auto max-w-7xl px-5 md:px-10">
        <div className="grid items-center gap-14 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <motion.p
              {...fadeUp(0.05)}
              className="flex items-center gap-3 font-mono text-[11px] uppercase tracking-[0.3em] text-muted-foreground"
              data-testid="hero-coordinates"
            >
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full animate-ping bg-accent opacity-60" />
                <span className="relative inline-flex h-2 w-2 bg-accent" />
              </span>
              22.2487° N — 72.1952° E · Gujarat, IN
            </motion.p>

            <KineticHeadline
              lines={lines}
              className="mt-7 font-display text-5xl font-light uppercase leading-[0.95] tracking-tighter md:text-7xl lg:text-[5.1rem]"
            />

            <motion.p
              {...fadeUp(0.7)}
              className="mt-8 max-w-md text-sm leading-relaxed text-muted-foreground md:text-base"
              data-testid="hero-subcopy"
            >
              Dholera SIR — 920 sq km of master-planned, title-screened land on the
              Delhi–Mumbai Industrial Corridor. Secure a surveyed parcel inside
              India&apos;s first greenfield smart city.
            </motion.p>

            <motion.div {...fadeUp(0.85)} className="mt-10 flex flex-wrap gap-3">
              <Button
                onClick={() => onNavigate("#parcels")}
                className="h-12 rounded-none px-7 font-mono text-xs uppercase tracking-[0.2em]"
                data-testid="hero-explore-btn"
              >
                Explore Parcels
                <ArrowUpRight className="ml-2 h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                onClick={onOpenCalc}
                className="h-12 rounded-none px-7 font-mono text-xs uppercase tracking-[0.2em]"
                data-testid="hero-calculator-btn"
              >
                <Calculator className="mr-2 h-4 w-4" />
                Run the Numbers
              </Button>
            </motion.div>
          </div>

          <motion.div
            style={{ x: frameX, y: frameY }}
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.5, ease: [0.16, 1, 0.3, 1] }}
            className="relative lg:col-span-5"
            data-testid="hero-figure"
          >
            <div className="relative border border-border bg-card p-2">
              <div className="relative h-[380px] overflow-hidden md:h-[500px]">
                <motion.img
                  src={activationAreaImg}
                  alt="Architectural model of the Dholera Activation Area"
                  style={{ y: imgY }}
                  className="h-[115%] w-full object-cover saturate-[.6] contrast-[1.05]"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-black/5 to-transparent" />
                <span className="absolute left-0 top-0 h-5 w-5 border-l-2 border-t-2 border-accent" />
                <span className="absolute right-0 top-0 h-5 w-5 border-r-2 border-t-2 border-accent" />
                <span className="absolute bottom-0 left-0 h-5 w-5 border-b-2 border-l-2 border-accent" />
                <span className="absolute bottom-0 right-0 h-5 w-5 border-b-2 border-r-2 border-accent" />
                <p className="absolute bottom-3 left-3 font-mono text-[10px] uppercase tracking-[0.3em] text-white/80">
                  Fig. 01 — Activation Area model
                </p>
              </div>
            </div>
            <div className="absolute -left-3 bottom-10 border border-border bg-card px-4 py-3 shadow-xl md:-left-8">
              <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground">
                India&apos;s First Greenfield City
              </p>
              <p className="mt-1 font-mono text-sm font-bold text-accent">
                First Designated &quot;Semicon City&quot;
              </p>
            </div>
          </motion.div>
        </div>

        <motion.div
          {...fadeUp(1.0)}
          className="mt-16 grid grid-cols-2 gap-px border border-border bg-border md:grid-cols-4"
          data-testid="hero-stats"
        >
          {STATS.map((s) => (
            <div key={s.label} className="bg-background p-5 md:p-6">
              <p className="font-display text-2xl font-extrabold tracking-tight md:text-4xl">
                {s.value}
              </p>
              <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground">
                {s.label}
              </p>
            </div>
          ))}
        </motion.div>
      </div>

      <div className="mt-16">
        <MarqueeStrip />
      </div>
    </section>
  );
}
